import { ExperimentStatus, ExperimentType } from "@prismancy/shared"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useNavigate } from "react-router-dom"
import Skeleton from "react-loading-skeleton"
import useCreateExperiment from "../../api/experiments/useCreateExperiment"
import useDeleteExperiment from "../../api/experiments/useDeleteExperiment"
import useExperiments from "../../api/experiments/useExperiments"
import { LIST_EXPERIMENTS_LAST_STATUS } from "../../constants"
import { Experiment } from "../../types"
import IconFont from "../elements/IconFont"
import { Modal, ModalConfirmationMessage } from "../elements/Modal"
import VariableDot from "../elements/VariableDot"
import useWorkspace from "../hooks/workspace/useWorkspace"
import { ExperimentList } from "./dashboard/ExperimentList"
import CreateExperimentMenu from "./dashboard/CreateExperimentMenu"
import { appRoutes } from "../../routes/appRoutes"

const getExperimentLoadMorePrefetchMargin = () => {
  const viewportHeight = typeof window === 'undefined' ? 600 : window.innerHeight
  return Math.round(Math.min(Math.max(viewportHeight * 0.65, 240), 720))
}

type DashboardProps = {
  status: ExperimentStatus
}

export const Dashboard: React.FC<DashboardProps> = ({ status }): React.ReactElement => {

  const navigate = useNavigate()
  const [selectedDeleteExperiment, setSelectedDeleteExperiment] = useState<Experiment | null>(null)
  const [deleteModalOpen, setDeleteModalOpen] = useState<boolean>(false)
  const [isCreatingExperiment, setIsCreatingExperiment] = useState<boolean>(false)
  const [loadMorePrefetchMargin, setLoadMorePrefetchMargin] = useState<number>(getExperimentLoadMorePrefetchMargin)
  // Calculate optimal page size based on viewport height
  const calculatePageSize = useMemo(() => {
    const EXPERIMENT_ITEM_HEIGHT = 80 // Approximate height of each experiment item
    const HEADER_HEIGHT = 50 // Header with "Experiments" title
    const TAB_HEADER_HEIGHT = 48 // Tab navigation
    const COUNT_SECTION_HEIGHT = 32 // Experiment count section
    const PADDING_BUFFER = 100 // Extra buffer for margins/padding

    const availableHeight = window.innerHeight - HEADER_HEIGHT - TAB_HEADER_HEIGHT - COUNT_SECTION_HEIGHT - PADDING_BUFFER
    const itemsThatFit = Math.floor(availableHeight / EXPERIMENT_ITEM_HEIGHT)

    // Add 5 extra items as buffer for smooth scrolling
    return Math.max(itemsThatFit + 5, 10) // Minimum of 10 items
  }, [])

  const {
    experiments,
    isLoading: isLoadingExperiments,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    totalCount
  } = useExperiments(status, calculatePageSize)
  const { workspace, isLoading: isLoadingWorkspace } = useWorkspace()
  const showRedirectTest = workspace?.redirect_test_enabled === true

  const createExperiment = useCreateExperiment()
  const deleteExperiment = useDeleteExperiment()
  const loadMoreRef = useRef<HTMLDivElement>(null)
  const loadMoreRequestPendingRef = useRef<boolean>(false)

  useEffect(() => {
    const handleResize = () => {
      setLoadMorePrefetchMargin(getExperimentLoadMorePrefetchMargin())
    }

    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  useEffect(() => {
    localStorage.setItem(LIST_EXPERIMENTS_LAST_STATUS, status)
  }, [status])

  const StatusIcons = {
    [ExperimentStatus.LIVE]: <><VariableDot status="live" /><span className="ml-1 text-xs text-state-success">Live</span></>,
    [ExperimentStatus.DRAFT]: <><VariableDot status="draft" /><span className="ml-1 text-xs text-state-warning">Draft</span></>,
    [ExperimentStatus.ENDED]: <><VariableDot status="ended" /><span className="ml-1 text-xs text-state-error">Ended</span></>,
  }

  const experimentsWithIcons = useMemo(() => {
    return experiments.map(experiment => {
      return {
        ...experiment,
        status_icon: StatusIcons[experiment.status]
      }
    })
  }, [experiments])

  const handleCreateExperiment = async (type: ExperimentType) => {
    if (isCreatingExperiment) return

    setIsCreatingExperiment(true)
    try {
      await createExperiment(
        type === ExperimentType.REDIRECT
          ? {
            type,
            redirect_settings: {
              targeting_mode: "specific",
              maintain_url_parameters_and_hashes: true,
              setup_completed: false,
            },
          }
          : { type }
      )
    } catch {
      // The mutation owns the user-facing error toast.
    } finally {
      setIsCreatingExperiment(false)
    }
  }

  const handleDeleteExperiment = async () => {
    if (selectedDeleteExperiment) {
      await deleteExperiment({ experimentId: selectedDeleteExperiment.id })
    }
    setSelectedDeleteExperiment(null)
    setDeleteModalOpen(false)
  }

  const handleCloseDeleteModal = async () => {
    setSelectedDeleteExperiment(null)
    setDeleteModalOpen(false)
  }

  const handleOnStatusChange = (status: ExperimentStatus) => {
    navigate(appRoutes.experiments.status(status), { viewTransition: true })
  }

  useEffect(() => {
    if (!isFetchingNextPage) {
      loadMoreRequestPendingRef.current = false
    }
  }, [isFetchingNextPage])

  const fetchNextExperimentsPage = useCallback(() => {
    if (hasNextPage && !isFetchingNextPage && !loadMoreRequestPendingRef.current) {
      loadMoreRequestPendingRef.current = true
      void fetchNextPage()
    }
  }, [fetchNextPage, hasNextPage, isFetchingNextPage])

  // Intersection observer for infinite scroll. The bottom root margin starts loading
  // before the user reaches the end of the list.
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const target = entries[0]
        if (target.isIntersecting) {
          fetchNextExperimentsPage()
        }
      },
      {
        root: null,
        rootMargin: `0px 0px ${loadMorePrefetchMargin}px 0px`,
        threshold: 0
      }
    )

    const currentRef = loadMoreRef.current
    if (currentRef) {
      observer.observe(currentRef)
    }

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef)
      }
    }
  }, [fetchNextExperimentsPage, loadMorePrefetchMargin])

  useEffect(() => {
    const currentRef = loadMoreRef.current
    if (!currentRef || !hasNextPage || isFetchingNextPage) return

    const frameId = window.requestAnimationFrame(() => {
      const triggerTop = currentRef.getBoundingClientRect().top
      const shouldLoadMore = triggerTop <= window.innerHeight + loadMorePrefetchMargin

      if (shouldLoadMore) {
        fetchNextExperimentsPage()
      }
    })

    return () => window.cancelAnimationFrame(frameId)
  }, [experiments.length, fetchNextExperimentsPage, hasNextPage, isFetchingNextPage, loadMorePrefetchMargin])

  const isLoading = isLoadingExperiments || isLoadingWorkspace
  const experimentCountLabel = {
    [ExperimentStatus.DRAFT]: "Draft",
    [ExperimentStatus.LIVE]: "Live",
    [ExperimentStatus.ENDED]: "Ended",
  }[status]

  return (
    <>
      <div className="ui-page-header-row">
        <h2 className="type-heading-lg">Experiments</h2>
        <CreateExperimentMenu
          isCreating={isCreatingExperiment}
          showRedirectTest={showRedirectTest}
          onSelect={handleCreateExperiment}
        />
      </div>

      <div className="mx-[-30px] border-y border-border-default">
        <div className="flex h-[36px] items-stretch justify-around">
          {([
            { s: ExperimentStatus.DRAFT, dotStatus: 'draft', label: 'Draft' },
            { s: ExperimentStatus.LIVE, dotStatus: 'live', label: 'Live' },
            { s: ExperimentStatus.ENDED, dotStatus: 'ended', label: 'Ended' },
          ] as const).map(({ s, dotStatus, label }) => {
            const active = status === s
            return (
              <button
                key={s}
                onClick={() => handleOnStatusChange(s)}
                className={`relative flex h-full w-full items-center justify-center gap-[4px] border-b border-transparent px-[12px] text-[12px] leading-[18px] transition-colors ${
                  active
                    ? 'text-heading font-medium'
                    : 'text-body-muted font-medium'
                }`}
              >
                <VariableDot status={dotStatus} className="shrink-0" />
                <span className="font-inherit leading-[18px]">{label}</span>
                {active && <div className="absolute inset-x-[14px] bottom-[-1px] h-px bg-border-active"></div>}
              </button>
            )
          })}
        </div>
      </div>

      {(experiments.length === 0 && !isLoading) &&
        <div className="flex flex-col items-center justify-center pt-[116px] text-center">
          <div className="flex h-[56px] w-[56px] items-center justify-center rounded-full bg-background-surface-02">
            <IconFont name="flask-conical" size={32} />
          </div>
          <p className="type-heading-md mt-[20px]">Create your first experiment</p>
          <p className="type-body mt-[8px] max-w-[240px]">Test two or more variants of a page</p>
          <CreateExperimentMenu
            isCreating={isCreatingExperiment}
            showRedirectTest={showRedirectTest}
            triggerVariant="link"
            onSelect={handleCreateExperiment}
          />
        </div>
      }

      {(experimentsWithIcons.length > 0 && workspace && !isLoading) &&
        <>
          <div className="my-[18px]">
            <span className="text-xs leading-4 text-body-muted">{totalCount} {experimentCountLabel}</span>
          </div>
          <ExperimentList experiments={experimentsWithIcons} workspace={workspace} />

          {/* Load more trigger */}
          <div ref={loadMoreRef} className="py-4">
            {isFetchingNextPage && (
              <div className="text-center">
                <Skeleton count={3} />
              </div>
            )}
          </div>
        </>
      }

      {isLoading === true && (
        <div className="mt-[18px] grid w-full grid-rows-[auto_auto_auto_auto_auto] gap-[14px]">
          <div className="py-1">
            <div className="h-[16px] w-[96px]"><Skeleton height={16} /></div>
          </div>
          <div className="border-b border-border-default pb-[14px]">
            <div className="h-[24px] w-full"><Skeleton height={24} /></div>
          </div>
          <div className="py-1">
            <div className="h-[16px] w-[96px]"><Skeleton height={16} /></div>
          </div>
          <div className="border-b border-border-default pb-[14px]">
            <div className="h-[24px] w-full"><Skeleton height={24} /></div>
          </div>
        </div>
      )}

      <Modal open={deleteModalOpen} onClose={handleCloseDeleteModal} title="Delete Experiment">
        <ModalConfirmationMessage>
          Are you sure you want to delete {selectedDeleteExperiment?.name}?
        </ModalConfirmationMessage>
        <div className="mt-[16px] flex justify-between gap-[16px]">
          <button type="button" className="btn-secondary" onClick={handleCloseDeleteModal}>Cancel</button>
          <button type="button" className="btn-danger" onClick={handleDeleteExperiment}>Delete Experiment</button>
        </div>
      </Modal>
    </>
  )
}
